import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ChevronRight } from 'lucide-react'
import type { HelpSearchResult } from '@/config/helpSections'
import { resolveHelpIcon } from './helpIcons'

interface HelpSearchResultsProps {
  query: string
  results: HelpSearchResult[]
  /** Open a section (optionally scrolled to a specific subsection). */
  onOpenSection: (sectionId: string, subsectionId?: string) => void
  /** Suggested category search terms shown when there are no results. */
  suggestions: string[]
  onSuggestion: (term: string) => void
}

/**
 * Search view for the Help Center: a flat, ranked list of matching sections with
 * the subsections that matched surfaced as quick links.
 */
export default function HelpSearchResults({
  query,
  results,
  onOpenSection,
  suggestions,
  onSuggestion,
}: HelpSearchResultsProps) {
  if (results.length === 0) {
    return (
      <div className="space-y-3" aria-live="polite" role="status">
        <p className="text-sm text-muted-foreground">
          No results found for &ldquo;{query}&rdquo;. Try one of these categories:
        </p>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((term) => (
            <button
              key={term}
              type="button"
              onClick={() => onSuggestion(term)}
              className="rounded-md bg-muted px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {term}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground" aria-live="polite" role="status">
        {results.length} {results.length === 1 ? 'result' : 'results'} for &ldquo;{query}&rdquo;
      </p>
      <ul className="space-y-3" role="list">
        {results.map(({ section, matchedSubsections }) => {
          const Icon = resolveHelpIcon(section.icon)
          return (
            <li key={section.id}>
              <Card className="transition-colors hover:border-primary/40">
                <CardContent className="p-4">
                  <button
                    type="button"
                    onClick={() => onOpenSection(section.id)}
                    className="group flex w-full items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
                    aria-label={`Open ${section.label} help`}
                  >
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1 font-medium">
                        {section.label}
                        <ChevronRight
                          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">
                        {section.description}
                      </span>
                    </span>
                  </button>
                  {matchedSubsections.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2 pl-8">
                      {matchedSubsections.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => onOpenSection(section.id, sub.id)}
                          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`Open ${sub.label} in ${section.label}`}
                        >
                          <Badge
                            variant="secondary"
                            className="cursor-pointer hover:bg-secondary/70"
                          >
                            {sub.label}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
